import {
  CurrencyInput,
  formatCurrencyFromCents,
  isCurrency,
} from '@/utils/currencyHelpers'

/**
 * Formats a currency input value to display as dollars or dashes
 * @param val - The currency input value (number or currency object)
 * @param showNeg - Whether to show negative sign for positive values
 * @returns Formatted currency string or dashes for zero/null values
 */
export const centsToDollarsOrDashes = (
  val: CurrencyInput,
  showNeg?: boolean
): string => {
  if (!val || (isCurrency(val) && val.value === 0)) {
    return '--'
  }

  const formatted = formatCurrencyFromCents(val)
  const isNegative = Number(val) < 0

  // If the value is already negative, formatCurrencyFromCents will include the minus sign
  // Only prepend minus if showNeg is true AND the value is positive
  if (showNeg && !isNegative) {
    return `-${formatted}`
  }

  return formatted
}

/**
 * Formats an amount subtracted from income or from what's owed — a deduction,
 * a credited payment, withholding. Zero is a real answer for all of these
 * (no payments logged, no standard deduction in that state, no QBI for an
 * S-corp), so it renders as $0.00 rather than the dashes that mean "we don't
 * have this number".
 */
export const centsToSubtractedAmount = (
  amountInCents: number | null | undefined
): string =>
  amountInCents === 0
    ? formatCurrencyFromCents(0)
    : centsToDollarsOrDashes(amountInCents, true)

/**
 * Formats an estimate headline, where zero is a real answer — a quarter the
 * calculation says there is nothing to pay — while an amount we don't have
 * still renders as dashes.
 */
export const centsToEstimateAmount = (
  amountInCents: number | null | undefined
): string =>
  amountInCents === 0
    ? formatCurrencyFromCents(0)
    : centsToDollarsOrDashes(amountInCents)
